import type {
  PlayerSummaryStats,
  ReportingPlayer
} from '@frontend/core/lib/domains/reporting'
import {
  getFullStatValue,
  getStatValue
} from '@frontend/core/lib/domains/reporting'
import { Fragment } from 'react'
import { formatStatValue } from '../../lib/format-cell'
import { useReportingState } from '../../lib/reporting-state'
import type { ReportingUnitType } from '../../lib/sport-config/types'
import type { StatInput } from './helpers'
import { formatSeasonAvg } from './helpers'

interface PlayerRow {
  player: ReportingPlayer
  stats: PlayerSummaryStats | null | undefined
}

interface TeamStatsPrintProps {
  playerRows: PlayerRow[]
  activeColumns: {
    key: string
    label: string
    unitType?: ReportingUnitType
  }[]
  totalStats: PlayerSummaryStats | null
  activeCategory: string
  viewModeLabel?: string
  categoryLabel?: string
}

/**
 * Print-only team stats table for PDF export. Hidden on screen via Tailwind
 * `hidden print:block`. Renders a plain HTML table with Tailwind classes.
 *
 * Season average sub-columns:
 * The API returns each stat as a StatValue with {value, seasonAvg, maxValue, minValue}.
 * The seasonAvg reflects the player's average across all sessions in the season.
 * With only one session, seasonAvg === value (this is correct — not a bug).
 *
 * NOTE: The Angular reporting tool's PDF appeared to show different season avg
 * numbers because its soccer-team-table-data.service.ts spreads mock data
 * (createStats() with Math.random()) as defaults before overriding with real
 * API data. Fields not yet implemented in buildCells() leaked random values
 * into the print output. Our implementation uses the real API data directly.
 */
export function TeamStatsPrint({
  playerRows,
  activeColumns,
  totalStats,
  activeCategory,
  viewModeLabel,
  categoryLabel
}: TeamStatsPrintProps) {
  const { config } = useReportingState()
  const { unitSystem } = config
  const columns = activeColumns

  if (activeCategory === 'compare') return null

  return (
    <div className='hidden print:block'>
      <style>{`@media print { @page { size: A4 landscape; margin: 5mm; } * { color: black !important; background: white !important; } }`}</style>
      {viewModeLabel && categoryLabel && (
        <p className='mb-2 text-sm'>
          Metric: {viewModeLabel}, {categoryLabel}
        </p>
      )}
      <table className='w-full border-collapse'>
        <thead>
          <tr className='border-b border-gray-400'>
            <th
              className='p-1 text-center align-bottom text-[8pt] font-normal whitespace-nowrap'
              rowSpan={2}
            >
              #
            </th>
            <th
              className='min-w-[5rem] p-1 text-left align-bottom text-[8pt] font-normal whitespace-nowrap'
              rowSpan={2}
            >
              Player
            </th>
            <th
              className='min-w-[2rem] p-1 text-center align-bottom text-[8pt] font-normal whitespace-nowrap'
              rowSpan={2}
            >
              Pos.
            </th>
            {columns.map((col) => (
              <th
                key={col.key}
                colSpan={2}
                className='border-l border-gray-400 p-1 text-center align-bottom text-[8pt] font-normal whitespace-nowrap'
              >
                {col.label.replace('\n', ' ')}
              </th>
            ))}
          </tr>
          <tr className='border-b-2 border-gray-400'>
            {columns.map((col) => (
              <Fragment key={col.key}>
                <th className='border-l border-gray-400 px-1 py-0.5 text-right text-[7pt] font-normal whitespace-nowrap'>
                  Value
                </th>
                <th className='px-1 py-0.5 text-right text-[7pt] font-normal whitespace-nowrap'>
                  Szn Avg
                </th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {playerRows.map(({ player, stats }) => {
            if (!player) return null
            return (
              <tr key={player.id} className='border-b border-gray-200'>
                <td className='p-1 text-center text-[8pt] whitespace-nowrap tabular-nums'>
                  {player.number}
                </td>
                <td className='p-1 text-left text-[8pt] whitespace-nowrap'>
                  {player.displayName ||
                    `${player.firstName} ${player.lastName}`}
                </td>
                <td className='p-1 text-center text-[8pt] whitespace-nowrap'>
                  {player.position || '-'}
                </td>
                {columns.map((col) => {
                  const s = stats as Record<string, unknown> | null | undefined
                  const raw = s?.[col.key] as StatInput
                  const val = getStatValue(raw)
                  const full = getFullStatValue(raw)
                  return (
                    <Fragment key={col.key}>
                      <td className='border-l border-gray-200 px-1 py-0.5 text-right text-[8pt] whitespace-nowrap tabular-nums'>
                        {formatStatValue(val, col.unitType, unitSystem)}
                      </td>
                      <td className='px-0.5 py-0.5 text-right text-[7pt] whitespace-nowrap tabular-nums'>
                        {full && full.seasonAvg != null
                          ? formatSeasonAvg(val, full, col.unitType, unitSystem)
                          : '-'}
                      </td>
                    </Fragment>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
        {totalStats && (
          <tfoot>
            <tr className='border-t-2 border-gray-400 font-bold'>
              <td
                className='p-1 text-center text-[8pt] whitespace-nowrap'
                colSpan={3}
              >
                Totals
              </td>
              {columns.map((col) => {
                const t = totalStats as Record<string, unknown> | null
                const raw = t?.[col.key] as StatInput
                return (
                  <Fragment key={col.key}>
                    <td className='border-l border-gray-200 px-1 py-0.5 text-right text-[8pt] font-bold whitespace-nowrap tabular-nums'>
                      {formatStatValue(
                        getStatValue(raw),
                        col.unitType,
                        unitSystem
                      )}
                    </td>
                    <td className='px-0.5 py-0.5 text-right text-[7pt] font-bold whitespace-nowrap tabular-nums'>
                      -
                    </td>
                  </Fragment>
                )
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}
